import { can, canAny, useAuthStore, useActiveTenant } from "@/stores/auth-store";
import { useEffectiveFeatures } from "@/lib/features";
import type { NavVisibilityContext } from "./nav-config";

/**
 * Builds the same `NavVisibilityContext` Sidebar.tsx and the header's quick
 * navigation search both need - one implementation so "can this signed-in
 * caller see this nav item" (RBAC + feature toggles + Super Admin isolation)
 * can never drift between the two call sites. Feature data is read once
 * here; React Query dedupes/caches it across every consumer regardless.
 */
export function useNavVisibility(): NavVisibilityContext {
  const user = useAuthStore((s) => s.user);
  const tenant = useActiveTenant();
  const { data: features, isLoading: featuresLoading } = useEffectiveFeatures();

  const isFeatureEnabled = (key?: string) => {
    if (!key) return true;
    if (featuresLoading || !features) return true; // fail open on the client - see lib/features.ts
    const f = features.find((x) => x.key === key);
    return f ? f.enabled : true;
  };

  return {
    isSuperAdmin: Boolean(user?.is_super_admin),
    hasTenant: Boolean(tenant),
    role: user?.role,
    employeeId: user?.employee_id,
    isFeatureEnabled,
    can,
    canAny,
  };
}
